/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"

import { isEcomBottomNavHiddenPath } from "constants/routes"
import { ddLogCustomError } from "utils/datadog"
import { setBottomNavVisibility } from "utils/nativeBridge"
import {
  isSessionErrorRaised,
  subscribeToSessionError,
} from "utils/sessionError"
import { isWebview } from "utils/webview"
import { sendWebviewBottomNavErrorEvent } from "utils/webviewTelemetry"

/**
 * Whether the app's bottom nav bar should be visible for the current route and
 * session state. Shared by route-level sync and overlays that temporarily hide
 * the bar (e.g. PMF feedback bottom sheet).
 */
export const getEcomBottomNavVisible = (pathname: string): boolean =>
  isSessionErrorRaised() || !isEcomBottomNavHiddenPath(pathname)

/**
 * Keeps the MEX app's bottom navigation bar in sync with the route.
 *
 * The eCom product list is hosted inside an app tab and keeps the bar; every
 * deeper product page is full screen and drops it. One webview renders both, so
 * something has to tell native which it is currently showing.
 *
 * **Derived from the route and asserted on every change, never toggled.**
 * Toggling from click handlers and unmount cleanups has two holes. Nothing
 * would assert the truth on a cold start: the bar's default is visible because
 * it is a tab, so a webview coming up already on a product page (process
 * restored at the last URL, deep link, reload after a crash) never mounts the
 * list, never fires "hide", and no code path would ever correct it. And unmount
 * cleanup is not guaranteed to run — not on a hard navigation, a reload, a
 * crash caught by an error boundary, or the WebView content process being
 * killed under memory pressure, which is exactly the low-end-device case this
 * shape exists to serve.
 *
 * Sending a value on EVERY navigation makes the call idempotent, lets the first
 * render fix a cold start into any route, and lets a dropped call be corrected
 * by the next route change.
 *
 * **One sanctioned toggle exists:** {@link useHideAppBottomNavWhileBottomSheetOpen}
 * temporarily hides the bar while an overlay (e.g. the PMF feedback bottom
 * sheet) is open and restores this hook's route-derived value on close. It is
 * the only caller that toggles; everything else asserts.
 *
 * **An active session error forces the bar back on, whatever the route says.**
 * `SessionErrorPage` renders over the current route without navigating, so on a
 * full-page product screen the pathname still reads "hide" while the merchant
 * is actually looking at the error screen. That screen's only control is Retry;
 * with the bar hidden too, a failed retry leaves them with no way out of the
 * webview at all — and on iOS there is not even a hardware back button. The
 * same fail-open reasoning as the route predicate, applied to the one state the
 * route cannot see.
 *
 * **Mount this ABOVE the auth gate, from a component that renders on every
 * route** — `RootRouter` today. That placement is load-bearing, not stylistic,
 * and two earlier homes were both wrong for the same underlying reason: an
 * unmount leaves the bar at its last asserted value with no later route change
 * to correct it. `NavigationContainer` matches only
 * `SUPPORTED_NAVIGATION_PATHS`, so leaving those prefixes unmounted it; `Home`
 * sits behind the auth gate, so a mid-session credential failure — the very
 * case that raises a session error — unmounted it while the bar was hidden.
 */
export const useAppBottomNavVisibility = (): void => {
  const { pathname } = useLocation()
  // A RE-RUN TRIGGER, not the value — the effect reads the latch itself below.
  // Deliberately not exposed as a `useSessionErrorActive()` boolean: anything
  // that reads this at render time is one commit stale in exactly the scenario
  // it exists for (the auth gate raises from its own mount effect, so this
  // render was handed `false` while the latch is already set). Kept private so
  // no second caller can pick up that trap. Seeded from the latch so a session
  // that died before this mounted does not spend a render, and a bridge call,
  // announcing a value it is about to correct.
  const [sessionErrorSeen, setSessionErrorSeen] = useState(isSessionErrorRaised)
  useEffect(() => subscribeToSessionError(() => setSessionErrorSeen(true)), [])
  // Per page load: this hook's host mounts once per load, so a ref is the
  // session latch — no module state to leak between loads or between tests.
  const hasReportedRef = useRef(false)

  useEffect(() => {
    if (!isWebview()) return

    const visible = getEcomBottomNavVisible(pathname)
    const hidden = !visible
    const call = setBottomNavVisibility(visible)

    // `undefined` (no bridge injected) is deliberately NOT reported. Inside the
    // webview it is overwhelmingly the documented cold-start race — the app has
    // not finished injecting `flutter_inappwebview` when the first route effect
    // runs — and since the latch fires on the first occurrence, reporting it
    // would spend the one record per load on the expected case. A rejection is
    // the actionable signal: the bridge is there and the app refused the call,
    // which means the handler is missing from this build or it threw.
    // The rejection value is deliberately not captured: it crosses the Dart
    // boundary as an opaque value that may carry a native message or stack, and
    // neither sink needs it to answer "which app builds refuse this handler?".
    call?.catch(() => {
      if (hasReportedRef.current) return
      hasReportedRef.current = true

      // Both sinks, same split as the session-error screen: Datadog Log is
      // where this alerts, Scribe is where it reads as a rate against
      // WEBVIEW_SESSION_START and slices by app version and platform.
      ddLogCustomError(
        "webview-bottom-nav-bridge",
        "Webview bottom nav bridge error",
        {
          context: "useAppBottomNavVisibility",
          hidden,
        },
      )
      sendWebviewBottomNavErrorEvent(hidden)
    })
    // `sessionErrorSeen` only schedules the re-run; on the auth-gate path that
    // costs one repeat of a value this effect already sent in the gate's own
    // commit. Left as-is rather than de-duplicated: the call is idempotent, and
    // while the app is still injecting the bridge a repeat is a free retry of a
    // first call that may have been dropped.
  }, [pathname, sessionErrorSeen])
}
